import React, { useMemo, useState } from 'react'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { type User } from '../../../models/User'

import { type IntegrationServiceId, getIntegrationDisplayName, sortIntegrationsByDisplayName } from '../../../common/integrations'
import { useCurrentUserIntegrations } from '../../Integration/useCurrentUserIntegrations'
import { useLastUsedDocumentConnector } from '../../../common/lastUsedDocumentConnector'
import { getLinkedFolderIds } from '../../../common/linkedDocumentFolders'
import { useContactLinkedFoldersByServiceId } from '../../../common/useContactLinkedFolder'
import { DocumentFolderMapSource, useLinkContactFolder } from '../../../common/useLinkContactFolder'

import { type FileBrowserItem, type FileBrowserSourceFolder } from '../../../components/DocumentFileBrowser/types'
import { type DocumentConnectorIntegration } from '../../../store/documentConnectors/documentConnectors'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { DocumentFileBrowserModal } from '../../../components/DocumentFileBrowser/DocumentFileBrowserModal'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from '../../../components/DocumentConnector/DocumentConnectorActionButton'
import { SetDefaultFolderBanner } from '../../../components/DocumentConnector/SetDefaultFolderBanner'

interface PendingDefaultFolder {
  serviceId: IntegrationServiceId
  contactId: string
  path: string
  integration: DocumentConnectorIntegration
}

interface ContactDocumentBrowseButtonsProps {
  contact: User | null
  onContactUpdated: (user: User) => void
  onFilesSelected: (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => void
  disabled?: boolean
}

export const ContactDocumentBrowseButtons: React.FC<ContactDocumentBrowseButtonsProps> = ({ contact, onContactUpdated, onFilesSelected, disabled = false }) => {
  const { trackingClient } = useTracking()

  const [activeServiceId, setActiveServiceId] = useState<IntegrationServiceId | null>(null)
  const [pendingDefaultFolder, setPendingDefaultFolder] = useState<PendingDefaultFolder | null>(null)
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()

  const { documentUploadSupportedIntegrations } = useCurrentUserIntegrations()

  const documentUploadSupportedIntegrationServiceIds = useMemo(
    () => documentUploadSupportedIntegrations.map(({ serviceId }) => serviceId),
    [documentUploadSupportedIntegrations],
  )
  const { folderIdsByServiceId: linkedFoldersByServiceId, isLoading: linkedFoldersLoading } = useContactLinkedFoldersByServiceId(
    documentUploadSupportedIntegrationServiceIds,
    contact?.id,
  )

  const handleLinkFolder = useLinkContactFolder(contact, DocumentFolderMapSource.IMPORT_DOCUMENT, onContactUpdated)

  if (documentUploadSupportedIntegrationServiceIds.length === 0) {
    return null
  }

  const handleFilesSelected = (serviceId: IntegrationServiceId, items: FileBrowserItem[], sourceFolder: FileBrowserSourceFolder | null) => {
    trackingClient.track({ eventName: TrackEvent.DOCUMENT_UPLOADED_FROM_EXTERNAL_SOURCE, eventDetails: { system: serviceId } })
    onFilesSelected(serviceId, items)

    if (contact && sourceFolder?.integration) {
      setPendingDefaultFolder({ serviceId, contactId: contact.id, path: sourceFolder.path, integration: sourceFolder.integration })
    }
  }

  const pendingFolderForContact = pendingDefaultFolder && contact && pendingDefaultFolder.contactId === contact.id ? pendingDefaultFolder : null
  const pendingLinkedFolderId = pendingFolderForContact && contact ? (getLinkedFolderIds(contact, pendingFolderForContact.serviceId)[0] ?? null) : null

  const items: DocumentConnectorActionItem[] = sortIntegrationsByDisplayName(documentUploadSupportedIntegrations).map(({ serviceId }) => ({
    key: serviceId,
    serviceId,
    label: `Browse in ${getIntegrationDisplayName(serviceId)}`,
  }))

  return (
    <>
      <div className='d-flex justify-content-center w-100'>
        <Tooltip id='browse-documents-tooltip' title={!contact ? 'Select a client first' : undefined}>
          <DocumentConnectorActionButton
            items={items}
            primaryKey={lastUsedServiceId ?? undefined}
            disabled={!contact || disabled}
            buttonClassName='button-medium'
            onSelect={(key) => {
              const serviceId = key as IntegrationServiceId
              recordLastUsedServiceId(serviceId)
              setActiveServiceId(serviceId)
            }}
          />
        </Tooltip>
      </div>
      {pendingFolderForContact && contact && !pendingLinkedFolderId ? (
        <SetDefaultFolderBanner
          className='w-100'
          serviceId={pendingFolderForContact.serviceId}
          actionText='Files added from'
          folderPath={pendingFolderForContact.path}
          contactFirstName={contact.firstName || getUserDisplayName(contact)}
          onSetDefault={() => handleLinkFolder(pendingFolderForContact.integration)}
        />
      ) : null}
      {activeServiceId ? (
        <DocumentFileBrowserModal
          show
          onHide={() => setActiveServiceId(null)}
          serviceId={activeServiceId}
          selectionMode='multi-file'
          initialFolderIds={linkedFoldersByServiceId[activeServiceId]}
          initialFoldersReady={!linkedFoldersLoading}
          onFilesSelected={(items, sourceFolder) => handleFilesSelected(activeServiceId, items, sourceFolder)}
        />
      ) : null}
    </>
  )
}
